import type { MRT_ColumnDef } from "mantine-react-table-open";
import { TransmissionSystemOperator } from "../../../../types/api.types";
import type { Connection, Person } from "../../../../utils/backend-types";
import { ObjectName } from "../../../../utils/enums";
import { ComponentOpenEditModalLink } from "../../ComponentOpenEditModalLink/ComponentOpenEditModalLink";
import type { ConfiguratorComponent } from "../ComponentTable";

const TRANSMISSION_SYSTEM_OPERATOR_LABEL_MAP: Record<
  TransmissionSystemOperator,
  string
> = {
  [TransmissionSystemOperator.Hertz]: "50Hertz Transmission GmbH",
  [TransmissionSystemOperator.Amprion]: "Amprion GmbH",
  [TransmissionSystemOperator.Tennet]: "TenneT TSO GmbH",
  [TransmissionSystemOperator.Transnet]: "TransnetBW GmbH"
};

interface GetConnectionCustomColumnsProps {
  persons?: Array<Person>;
  projectId: string;
  siteId: number;
}

function getConnectionCustomColumns({
  persons,
  projectId,
  siteId
}: GetConnectionCustomColumnsProps): Array<
  MRT_ColumnDef<ConfiguratorComponent>
> {
  return [
    {
      header: "Netzanschlussnehmer",
      accessorFn: (rowData) =>
        persons?.find(
          (person) => person.id === (rowData as Connection).connectingParty
        )?.name || (rowData as Connection).connectingParty,
      Cell: ({ cell, row }) => {
        const connectingParty = (row.original as Connection).connectingParty;
        return (
          connectingParty && (
            <ComponentOpenEditModalLink
              componentId={connectingParty}
              linkContent={cell.getValue<string | number>()}
              objectName={ObjectName.Person}
              projectId={projectId}
              siteId={siteId}
            />
          )
        );
      }
    },
    {
      header: "Strombezugsvertrag gehalten durch",
      accessorFn: (rowData) =>
        persons?.find(
          (person) => person.id === (rowData as Connection).supplyContractHeldBy
        )?.name || (rowData as Connection).supplyContractHeldBy,
      Cell: ({ cell, row }) => {
        const supplyContractHeldBy = (row.original as Connection)
          .supplyContractHeldBy;
        return (
          supplyContractHeldBy && (
            <ComponentOpenEditModalLink
              componentId={supplyContractHeldBy}
              linkContent={cell.getValue<string | number>()}
              objectName={ObjectName.Person}
              projectId={projectId}
              siteId={siteId}
            />
          )
        );
      }
    },
    {
      header: "Anschlussnetzbetreiber",
      accessorKey: "gridOperatorName"
    },
    {
      header: "Übertragungsnetzbetreiber",
      accessorFn: (rowData) => {
        const transmissionSystemOperator = (rowData as Connection)
          .transmissionSystemOperator;
        return (
          transmissionSystemOperator &&
          TRANSMISSION_SYSTEM_OPERATOR_LABEL_MAP[transmissionSystemOperator]
        );
      }
    }
  ];
}

export { getConnectionCustomColumns };
